import { AsyncPipe } from '@angular/common'
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
} from '@angular/core'

import { TranslatePipe } from '@ngx-translate/core'
import { Dialog } from 'primeng/dialog'

import { ApiErrors, CountryCodes, UserProfile } from '@ti/contracts'
import { DeviceService, ErrorHandler } from '@ti/core/common/services'
import { getApiErrorKey, getMessageFromException } from '@ti/core/common/utils'
import { LanguageService } from '@ti/core/ui-kit/i18n'

import { UserFormMode, UserFormModel } from '../../models/user-form.model'
import { BaseUserManager } from '../../services/user.manager'
import { UserFormComponent } from '../user-form/user-form.component'

@Component({
  selector: 'app-edit-user-dialog',
  templateUrl: 'user-edit.component.html',
  styleUrl: 'user-edit.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [UserFormModel],
  imports: [AsyncPipe, Dialog, TranslatePipe, UserFormComponent],
})
export class UserEditDialogComponent {
  @Output() closed = new EventEmitter<void>()
  @Output() updated = new EventEmitter<void>()

  @Input() opened = false
  @Input() user?: UserProfile
  @Input() mode: UserFormMode = UserFormMode.Fleet
  @Input() country: CountryCodes = CountryCodes.UnitedStates
  @Input() usernamePrefix?: string

  protected readonly cd = inject(ChangeDetectorRef)
  protected readonly deviceService = inject(DeviceService)
  protected readonly form = inject(UserFormModel)
  protected readonly userManager = inject(BaseUserManager)
  protected readonly errorHandler = inject(ErrorHandler)
  protected readonly languageService = inject(LanguageService)

  public open() {
    this.opened = true
    this.cd.markForCheck()
  }

  public close() {
    this.opened = false
    this.cd.markForCheck()
    this.closed.emit()
  }

  protected async updateUser(data: Partial<UserProfile> & { password?: string }) {
    if (!this.user) return

    try {
      this.form.setIsLoading(true)
      await this.userManager.update(this.user, data)
      this.updated.emit()
      this.close()
    } catch (e: unknown) {
      console.warn(`Cannot update user`, e)

      const errorMsg = getMessageFromException(e)
      if (errorMsg === ApiErrors.authUserWithSameEmailExists) {
        this.form.setEmailError(await this.languageService.translate(getApiErrorKey(errorMsg)))
      } else {
        await this.errorHandler.notifyUser(e, 'Cannot update user')
      }
    } finally {
      this.form.setIsLoading(false)
    }
  }
}
